if (!customElements.get('prime-day-offers')) {
  class PrimeDayOffers extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.buttons = [];
      this.timelinePoints = [];
      this.panels = [];
      this.timeline = null;
      this.tabScroller = null;
      this.panelContainer = null;
      this.prevButton = null;
      this.nextButton = null;
      this.abortController = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerDeltaX = 0;
      this.pointerDeltaY = 0;
      this.activePointerId = null;
      this.gestureElement = null;
      this.suppressClickUntil = 0;
      this.progressAnimationTimer = null;

      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.buttons = Array.from(this.querySelectorAll('.prime-day-offers__tab[data-prime-day-index]'));
      this.timelinePoints = Array.from(this.querySelectorAll('.prime-day-offers__timeline-point[data-prime-day-index]'));
      this.panels = Array.from(this.querySelectorAll('[data-prime-day-panel]'));
      this.timeline = this.querySelector('.prime-day-offers__timeline');
      this.tabScroller = this.querySelector('[data-prime-day-tabs-scroller]');
      this.panelContainer = this.querySelector('.prime-day-offers__panels');
      this.prevButton = this.querySelector('[data-prime-day-prev]');
      this.nextButton = this.querySelector('[data-prime-day-next]');
      this.disableCardImageDrag();

      if (!this.buttons.length) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      [...this.buttons, ...this.timelinePoints].forEach((button) => {
        button.addEventListener('click', () => this.setActiveIndex(Number(button.dataset.primeDayIndex), true), { signal });
      });

      this.buttons.forEach((button) => {
        button.addEventListener('keydown', (event) => this.onTabKeydown(event), { signal });
      });

      this.prevButton?.addEventListener('click', () => this.setActiveIndex(this.activeIndex - 1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.setActiveIndex(this.activeIndex + 1, true), { signal });
      [this.tabScroller, this.panelContainer].forEach((element) => {
        if (!element) return;
        element.addEventListener('pointerdown', this.onPointerDown, { signal });
        element.addEventListener('pointermove', this.onPointerMove, { signal });
        element.addEventListener('pointerup', this.onPointerUp, { signal });
        element.addEventListener('pointercancel', this.onPointerCancel, { signal });
        element.addEventListener('click', (event) => this.onGestureClick(event), { capture: true, signal });
      });
      window.addEventListener('resize', this.onResize, { passive: true, signal });

      const selectedIndex = this.buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
      this.setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0, false);
    }

    disableCardImageDrag() {
      this.querySelectorAll('.prime-day-offers__card-image').forEach((image) => {
        image.draggable = false;
        image.addEventListener('dragstart', (event) => event.preventDefault());
      });
    }

    disconnectedCallback() {
      if (!this.abortController) return;
      this.abortController.abort();
      this.abortController = null;
      if (this.progressAnimationTimer) {
        window.clearTimeout(this.progressAnimationTimer);
        this.progressAnimationTimer = null;
      }
    }

    setActiveIndex(index, shouldFocus = false) {
      const count = this.buttons.length;
      if (!count) return;

      this.activeIndex = Math.max(0, Math.min(index, count - 1));
      const progress = count === 1 ? 1 : this.activeIndex / (count - 1);
      this.style.setProperty('--prime-day-progress', progress);
      this.playProgressAnimation();

      this.buttons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === this.activeIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
        if (isActive && shouldFocus) button.focus({ preventScroll: true });
      });

      this.timelinePoints.forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', buttonIndex === this.activeIndex);
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === this.activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
      });

      if (this.prevButton) this.prevButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === count - 1;

      this.updateTimelineMetrics();
      this.centerActiveTab();
    }

    playProgressAnimation() {
      if (this.prefersReducedMotion()) return;

      this.classList.remove('is-progress-switching');
      window.clearTimeout(this.progressAnimationTimer);
      this.getBoundingClientRect();
      this.classList.add('is-progress-switching');
      this.progressAnimationTimer = window.setTimeout(() => {
        this.classList.remove('is-progress-switching');
        this.progressAnimationTimer = null;
      }, 520);
    }

    updateTimelineMetrics() {
      if (!this.timeline || !this.timelinePoints.length) return;

      const timelineRect = this.timeline.getBoundingClientRect();
      if (!timelineRect.width) return;

      const getPointCenter = (point) => {
        const dot = point.querySelector('.prime-day-offers__timeline-dot') || point;
        const rect = dot.getBoundingClientRect();
        return rect.left - timelineRect.left + rect.width / 2;
      };

      const firstPoint = this.timelinePoints[0];
      const lastPoint = this.timelinePoints[this.timelinePoints.length - 1] || firstPoint;
      const activePoint = this.timelinePoints[this.activeIndex] || firstPoint;
      const start = getPointCenter(firstPoint);
      const end = getPointCenter(lastPoint);
      const active = getPointCenter(activePoint);
      const width = Math.max(0, end - start);

      this.timeline.style.setProperty('--prime-day-track-start', `${start}px`);
      this.timeline.style.setProperty('--prime-day-track-width', `${width}px`);
      this.timeline.style.setProperty('--prime-day-thumb-x', `${active}px`);
    }

    centerActiveTab() {
      if (!this.tabScroller) return;
      const activeButton = this.buttons[this.activeIndex];
      if (!activeButton) return;

      const targetLeft = activeButton.offsetLeft + activeButton.offsetWidth / 2 - this.tabScroller.clientWidth / 2;
      this.tabScroller.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    onTabKeydown(event) {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          this.setActiveIndex(this.activeIndex + 1, true);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.setActiveIndex(this.activeIndex - 1, true);
          break;
        case 'Home':
          event.preventDefault();
          this.setActiveIndex(0, true);
          break;
        case 'End':
          event.preventDefault();
          this.setActiveIndex(this.buttons.length - 1, true);
          break;
      }
    }

    onPointerDown(event) {
      if (!this.isMobileViewport()) return;
      if (event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('[data-prime-day-prev], [data-prime-day-next]')) return;

      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDeltaX = 0;
      this.pointerDeltaY = 0;
      this.activePointerId = event.pointerId;
      this.gestureElement = event.currentTarget;
      this.gestureElement.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.activePointerId) return;
      this.pointerDeltaX = event.clientX - this.pointerStartX;
      this.pointerDeltaY = event.clientY - this.pointerStartY;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.activePointerId) return;

      const distanceX = Math.abs(this.pointerDeltaX);
      const distanceY = Math.abs(this.pointerDeltaY);
      const isHorizontalSwipe = distanceX > 42 && distanceX > distanceY * 1.15;

      if (isHorizontalSwipe) {
        this.suppressClickUntil = Date.now() + 450;
        this.setActiveIndex(this.pointerDeltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1, false);
      }
      this.resetPointer();
    }

    onPointerCancel(event) {
      if (event.pointerId !== this.activePointerId) return;
      this.resetPointer();
    }

    resetPointer() {
      if (this.gestureElement?.hasPointerCapture?.(this.activePointerId)) {
        this.gestureElement.releasePointerCapture(this.activePointerId);
      }
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerDeltaX = 0;
      this.pointerDeltaY = 0;
      this.activePointerId = null;
      this.gestureElement = null;
    }

    onGestureClick(event) {
      if (Date.now() > this.suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }

    onResize() {
      window.requestAnimationFrame(() => {
        this.updateTimelineMetrics();
        this.centerActiveTab();
      });
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 699px)').matches;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('prime-day-offers', PrimeDayOffers);
}
